import JSZip from 'jszip';
import { MAP_COLORS, MAP_GRAPHIC_XML, MAP_RELS, MAP_STYLE } from './constants';
import { PPTgenerateGeographyMapXml } from './pptUtils';
import { getCssColor, rgbToHex } from '../../widget-utils/widgetUtils';
import { UnionBuffer } from '../../../../models/file.model';
import { fixAllRelsInZip } from './security';

/** Escape special XML characters to prevent invalid OOXML output. */
function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

interface InjectMapChartXmlParams {
  fileName: string;
  buffer: UnionBuffer;
}

export async function injectMapChart({ buffer }: InjectMapChartXmlParams): Promise<Blob> {
  const zip = await JSZip.loadAsync(buffer);

  // Loop over all slides
  const slideFilePaths = Object.keys(zip.files).filter(
    (f) => f.startsWith('ppt/slides/slide') && f.endsWith('.xml')
  );
  const parser = new DOMParser();

  for (const path of slideFilePaths) {
    const xml = await zip.file(path)?.async('string');

    // Look for table with name="geography" or text content that matches
    if (xml?.includes('geographyTable')) {
      const xmlDoc = parser.parseFromString(xml, 'application/xml');
      const tables = xmlDoc.getElementsByTagName('a:tbl');
      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        const rows = table.getElementsByTagName('a:tr');
        const extractedData: { [key: string]: number } = {};

        for (let r = 0; r < rows.length; r++) {
          const row = rows[r];
          const cells = row.getElementsByTagName('a:tc');
          let key = '';
          let value = '';
          for (let c = 0; c < cells.length; c++) {
            const cell = cells[c];
            const textNode = cell.getElementsByTagName('a:t');
            if (c === 0) key = textNode[0]?.textContent || '';
            else value = textNode[0]?.textContent || '';
          }
          extractedData[key] = parseInt(value) || 0;
        }

        const mapXml = await PPTgenerateGeographyMapXml(extractedData);
        zip.file('ppt/charts/chartex1.xml', mapXml);
        zip.file('ppt/charts/colors1.xml', MAP_COLORS);
        zip.file('ppt/charts/style1.xml', MAP_STYLE);
        zip.file('ppt/charts/_rels/chartex1.xml.rels', MAP_RELS);
      }

      const newFrameXml = MAP_GRAPHIC_XML;

      // Replace the first <p:graphicFrame>...</p:graphicFrame> with newFrameXml
      const slideXml = xml
        .replace(/<p:graphicFrame[\s\S]*?<\/p:graphicFrame>/, newFrameXml)
        .replace(/<p:sld([^>]*)>/, (_, attrs) => {
          const required = {
            'xmlns:mc': '"http://schemas.openxmlformats.org/markup-compatibility/2006"',
            'xmlns:cx': '"http://schemas.microsoft.com/office/drawing/2014/chartex"',
            'xmlns:cx1': '"http://schemas.microsoft.com/office/drawing/2015/9/8/chartex"',
            'xmlns:p14': '"http://schemas.microsoft.com/office/powerpoint/2010/main"',
          };
          for (const key in required) {
            const regex = new RegExp(`${key}="[^"]*"`, 'g');
            attrs = attrs.replace(regex, '');
          }
          const injected = Object.entries(required)
            .map(([key, val]) => ` ${key}=${val}`)
            .join('');
          return `<p:sld${attrs}${injected}>`;
        })
        .replace(
          '</p:cSld>',
          `<p:extLst><p:ext uri="{BB962C8B-B14F-4D97-AF65-F5344CB8AC3E}"><p14:creationId xmlns:p14="http://schemas.microsoft.com/office/powerpoint/2010/main" val="2688023721" /></p:ext></p:extLst></p:cSld>`
        )
        .replace(
          '</p:sld>',
          `<mc:AlternateContent xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"><mc:Choice xmlns:p14="http://schemas.microsoft.com/office/powerpoint/2010/main" Requires="p14"><p:transition p14:dur="700" spd="med"><p:fade /></p:transition></mc:Choice><mc:Fallback><p:transition spd="med"><p:fade /></p:transition></mc:Fallback></mc:AlternateContent><p:timing><p:tnLst><p:par><p:cTn id="1" restart="never" nodeType="tmRoot" /></p:par></p:tnLst></p:timing></p:sld>`
        );

      const parsedSlideXml = parser.parseFromString(slideXml, 'application/xml');
      const slideXmlString = new XMLSerializer().serializeToString(parsedSlideXml);
      zip.file(path, slideXmlString);

      // Patch slide rels and theme
      const relsPath = path.replace('slides/', 'slides/_rels/') + '.rels';
      const relsXml = await zip.file(relsPath)?.async('string');
      const themeXml = await zip.file('ppt/theme/theme1.xml')?.async('string');

      const { r, g, b } = getCssColor('--kendo-color-series-map-scale-end');
      const mapHexColor = rgbToHex(`rgb(${r}, ${g}, ${b})`);
      const patchedThemeXml = themeXml?.replace(`val="4472C4"`, `val="${mapHexColor}"`);
      if (patchedThemeXml) zip.file('ppt/theme/theme1.xml', patchedThemeXml);

      const patchedRelsXml = relsXml?.replace(
        '</Relationships>',
        `<Relationship Id="rId5" Type="http://schemas.microsoft.com/office/2014/relationships/chartEx" Target="../charts/chartex1.xml" /></Relationships>`
      );
      if (patchedRelsXml) zip.file(relsPath, patchedRelsXml);

      const contentTypesXml = await zip.file('[Content_Types].xml')?.async('string');
      const patchedContentTypesXml = contentTypesXml?.replace(
        '</Types>',
        `<Override PartName="/ppt/charts/chartex1.xml" ContentType="application/vnd.ms-office.chartex+xml"/><Override PartName="/ppt/charts/colors1.xml" ContentType="application/vnd.ms-office.chartcolorstyle+xml"/><Override PartName="/ppt/charts/style1.xml" ContentType="application/vnd.ms-office.chartstyle+xml"/></Types>`
      );
      if (patchedContentTypesXml) zip.file('[Content_Types].xml', patchedContentTypesXml);

      break;
    }
  }

  // Patch bar charts: inject per-point custom percentage labels
  await patchBarChartPercentLabels(zip);

  // Drain any unconsumed entries so they don't leak into a subsequent export
  clearBarChartDisplayLabels();

  //normalize all .rels targets (relative -> absolute) so we can satisfy Azure WAF rules
  await fixAllRelsInZip(zip);

  // Save new file
  const finalBuffer = await zip.generateAsync({ type: 'blob' });
  return finalBuffer;
}

// ---- Bar chart per-point label registry ----
// Bridges the gap between the JS renderer (which knows displayValues) and the
// PPTX XML post-processor (which modifies the ZIP). Entries are pushed by the
// renderer and consumed in order by the post-processor.

export interface BarChartDisplayLabelEntry {
  seriesLabels: string[][]; // [seriesIdx][pointIdx] = "32.2%"
  fontSize: number; // hundredths of a point, e.g. 900 = 9pt
  fontColor: string; // 6-digit hex without '#', e.g. "00AAFF"
  fontFace: string; // e.g. "Inter"
  labelPosition?: string; // OOXML dLblPos value, e.g. "inEnd" for total labels
  layoutOffset?: { x: string; y: string }; // manual offset to nudge label outside bar/column
}

const pendingBarChartLabels: BarChartDisplayLabelEntry[] = [];

export function pushBarChartDisplayLabels(entry: BarChartDisplayLabelEntry): void {
  pendingBarChartLabels.push(entry);
}

/** Drain the queue so stale entries don't leak across export retries. */
export function clearBarChartDisplayLabels(): void {
  pendingBarChartLabels.length = 0;
}

function shiftBarChartDisplayLabels(): BarChartDisplayLabelEntry | undefined {
  return pendingBarChartLabels.shift();
}

/**
 * Post-process bar chart XML to inject per-point custom data labels.
 *
 * pptxgenjs uses the same data values for bar sizing AND labels, making it
 * impossible to show displayValue labels with value-based bar proportions.
 * We work around this by:
 *  1. Using `value` as chart data (correct bar proportions)
 *  2. Emitting a sentinel format code (`__SHOW_PERCENT__`) in the renderer
 *  3. Injecting per-point `<c:dLbl>` elements with custom rich text containing
 *     the displayValue percentage (e.g. "32.2%") via PPTX XML post-processing
 *  4. Disabling auto-generated value labels (showVal=0) since custom labels
 *     handle the display
 */
async function patchBarChartPercentLabels(zip: JSZip): Promise<void> {
  const SENTINEL = '__SHOW_PERCENT__';

  const chartPaths = Object.keys(zip.files).filter(
    (f) => f.startsWith('ppt/charts/chart') && f.endsWith('.xml')
  );

  for (const chartPath of chartPaths) {
    const xml = await zip.file(chartPath)?.async('string');
    if (!xml || !xml.includes(SENTINEL)) continue;

    const entry = shiftBarChartDisplayLabels();
    if (!entry) continue;

    let patched = xml;

    // Replace sentinel format code with General (labels are now custom text)
    patched = patched.replace(new RegExp(SENTINEL, 'g'), 'General');

    // Disable auto-generated value labels (per-point custom labels handle display)
    patched = patched.replace(/<c:showVal val="1"\/>/g, '<c:showVal val="0"/>');

    // Inject per-point <c:dLbl> elements INSIDE each series-level <c:dLbls> block.
    // Per OOXML spec (CT_DLbls), <c:dLbl> children come first, before <c:numFmt>.
    // pptxgenjs generates one <c:dLbls> per <c:ser> (series-level) plus one at the
    // chart level. We only inject into the first N (one per series), skipping the
    // chart-level block.
    let seriesIdx = 0;
    const totalSeries = entry.seriesLabels.length;
    patched = patched.replace(/<c:dLbls>/g, (match) => {
      if (seriesIdx >= totalSeries) return match; // chart-level dLbls, skip
      const labels = entry.seriesLabels[seriesIdx++];
      if (!labels || labels.length === 0) return match;

      const dLblXml = labels
        .map((text, pointIdx) => {
          if (!text) return '';
          return buildPerPointLabelXml(
            pointIdx,
            text,
            entry.fontSize,
            entry.fontColor,
            entry.fontFace,
            entry.labelPosition,
            entry.layoutOffset
          );
        })
        .join('');

      return match + dLblXml;
    });

    zip.file(chartPath, patched);
  }
}

function buildPerPointLabelXml(
  pointIdx: number,
  text: string,
  fontSize: number,
  fontColor: string,
  fontFace: string,
  labelPosition?: string,
  layoutOffset?: { x: string; y: string }
): string {
  const posXml = labelPosition ? `<c:dLblPos val="${labelPosition}"/>` : '';
  // Manual layout offset to nudge label outside the bar/column (used for total labels)
  const layoutXml = layoutOffset
    ? `<c:layout><c:manualLayout><c:x val="${layoutOffset.x}"/><c:y val="${layoutOffset.y}"/></c:manualLayout></c:layout>`
    : '';
  return (
    `<c:dLbl>` +
    `<c:idx val="${pointIdx}"/>` +
    layoutXml +
    `<c:tx><c:rich>` +
    `<a:bodyPr/>` +
    `<a:lstStyle/>` +
    `<a:p>` +
    `<a:pPr>` +
    `<a:defRPr b="0" i="0" strike="noStrike" sz="${fontSize}" u="none">` +
    `<a:solidFill><a:srgbClr val="${fontColor}"/></a:solidFill>` +
    `<a:latin typeface="${fontFace}"/>` +
    `</a:defRPr>` +
    `</a:pPr>` +
    `<a:r>` +
    `<a:rPr lang="en-US" dirty="0" sz="${fontSize}"><a:solidFill><a:srgbClr val="${fontColor}"/></a:solidFill><a:latin typeface="${fontFace}"/></a:rPr>` +
    `<a:t>${escapeXml(text)}</a:t>` +
    `</a:r>` +
    `</a:p>` +
    `</c:rich></c:tx>` +
    posXml +
    `<c:showLegendKey val="0"/>` +
    `<c:showVal val="0"/>` +
    `<c:showCatName val="0"/>` +
    `<c:showSerName val="0"/>` +
    `<c:showPercent val="0"/>` +
    `<c:showBubbleSize val="0"/>` +
    `</c:dLbl>`
  );
}
